import { Checkbox, FormControl, FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import MDButton from "components/MDButton";
import { useCallback, useEffect, useState } from "react";
import { getTAPickSlip, taskUnallocate, taskConfirm } from "services/data.outbound.service";

import { Success, Error_, LoadingDialog } from "ui-components/Alert";
import { TableCard } from "ui-components/Layout";
import CheckboxTable2 from "ui-components/tables/CheckboxTable2";
import PickslipLog from "./PickslipLog";
import { CheckboxHeader, CheckboxCell } from "ui-components/tables/components/table";
import { PrintButton } from "ui-components/IconButton";
import { getPickslipReport } from "services/data.report.service";
import MDBadge from "components/MDBadge";
import PickslipPickScanLog from "./PickslipPickScanLog";
import PickslipPackScanLog from "./PickslipPackScanLog";
import { MessageDialog } from "ui-components/Alert";
import { getWaveReport } from "services/data.report.service";
import { Texts } from "ui-components/Form";
import { CustomPrintButton } from "ui-components/IconButton";

function Pickslip({ customerCode, setAlert }) {
  const [pickSlipList, setPickSlipList] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [logRequest, setLogRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const logProp = { open, setOpen, logRequest, setLogRequest };
  const [pickLogRequest, setPickLogRequest] = useState(null);
  const [pickOpen, setPickOpen] = useState(false);
  const [packLogRequest, setPackLogRequest] = useState(null);
  const [packOpen, setPackOpen] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [basketId, setBasketId] = useState("");
  const [type, setType] = useState("ALL");
  //console.log(user.userId)
  const pickLogProp = {
    open: pickOpen,
    setOpen: setPickOpen,
    logRequest: pickLogRequest,
    setLogRequest: setPickLogRequest,
  };
  const packLogProp = {
    open: packOpen,
    setOpen: setPackOpen,
    logRequest: packLogRequest,
    setLogRequest: setPackLogRequest,
  };
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const downloadReportMutation = useMutation(getPickslipReport, {
    onSuccess: (response) => {
      const fileType = response.headers["content-type"];
      const blob = new Blob([response.data], {
        type: fileType,
      });
      const url = window.URL.createObjectURL(blob);
      if (fileType.includes("pdf")) {
        const w = window.open(url);
        w.print();
        //a.href = null;
        window.URL.revokeObjectURL(url);
      }
    },
    onError: () => setAlert(<Error_ content="The report file not found" onChange={setAlert} />),
  });

  const downloadWaveReportMutation = useMutation(getWaveReport, {
    onSuccess: (response) => {
      const fileType = response.headers["content-type"];
      const blob = new Blob([response.data], {
        type: fileType,
      });
      const url = window.URL.createObjectURL(blob);
      if (fileType.includes("pdf")) {
        const w = window.open(url);
        w.print();
        //a.href = null;
        window.URL.revokeObjectURL(url);
      }
    },
    onError: () => setAlert(<Error_ content="The report file not found" onChange={setAlert} />),
  });
  const columns = [
    {
      accessorKey: "orderDate",
      header: "Order Date",
      width: 120,
    },
    {
      accessorKey: "pickslipNumber",
      header: "Pickslip Number",
      width: 140,
      cell: ({ row }) => {
        const { id, pickslipNumber } = row.original;
        return (
          <span
            style={{
              color: "#1A73E8",
              fontWeight: 400,
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => {
              setOpen(true);
              setLogRequest({ pickslipId:id, pickslipNumber, customerCode });
            }}
          >
            {pickslipNumber}
          </span>
        );
      },
    },
    {
      accessorKey: "waveNumber",
      header: "Wave Number",
      width: 140,
    },
    {
      accessorKey: "custOrdNbr",
      header: "Cust Order Number",
      width: 155,
    },
    {
      accessorKey: "referenceNo",
      header: "Reference No",
      width: 150,
    },
    {
      accessorKey: "marketplace",
      header: "Marketplace",
      width: 100,
    },
    {
      accessorKey: "orderNumber",
      header: "Order Number",
      width: 140,
      //enableColumnFilter:true,
      filterFn: (row, id, orders) => {
        const order = row.getValue(id);
        console.log(order);
        return orders.includes(order);
      },
    },
    {
      accessorKey: "createdBy",
      header: "Created By",
      width: 120,
    },
    {
      accessorKey: "createdOn",
      header: "Created On",
      width: 120,
    },
    {
      accessorKey: "pickingStatus",
      header: "Picking Status",
      width: 120,
      cell: ({ row }) => {
        const { pickslipNumber, pickingStatus } = row.original;
        const onClick = () => {
          setPickOpen(true);
          setPickLogRequest({ pickslipNumber });
        };
        if (pickingStatus === "O")
          return <Badge color="warning" content="Scanning" onClick={onClick} />;
        if (pickingStatus === "P")
          return <Badge color="success" content="Complete" onClick={onClick} />;
        return <div></div>;
      },
    },
    {
      accessorKey: "packingStatus",
      header: "Packing Status",
      width: 120,
      cell: ({ row }) => {
        const { pickslipNumber, packingStatus } = row.original;
        const onClick = () => {
          setPackOpen(true);
          setPackLogRequest({ pickslipNumber });
        };
        if (packingStatus === "A")
          return <Badge color="warning" content="Scanning" onClick={onClick} />;
        if (packingStatus === "P")
          return <Badge color="success" content="Complete" onClick={onClick} />;
        return <div></div>;
      },
    },
    {
      accessorKey: "userId",
      header: "User Id",
      width: 120,
    },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => (
        <>
          <PrintButton
            title="print pickslip"
            onClick={() => {
              if (Boolean(row.original.waveNumber)) {
                downloadWaveReportMutation.mutate({
                  customerCode,
                  waveNumber: row.original.waveNumber,
                  reportType: "PDF",
                });
              } else {
                downloadReportMutation.mutate({
                  customerCode,
                  pickslipNumber: row.original.pickslipNumber,
                  reportType: "PDF",
                  reportName: "Pickslip",
                });
              }
            }}
          />
          {Boolean(row.original.waveNumber) && (
            <CustomPrintButton
              color="default"
              variant="gradient"
              size="small"
              title="print wave summary"
              onClick={() => {
                downloadWaveReportMutation.mutate({
                  customerCode,
                  waveNumber: row.original.waveNumber,
                  reportType: "WavePickslip",
                });
              }}
            />
          )}
        </>
      ),
    },
  ];

  const columns1 = [
    {
      id: "select",
      ...(type === "CANCEL" && { header: CheckboxHeader }),
      cell: CheckboxCell,
      // onChange={() => {
      //   const { id, original } = props.row;
      //   let ids = [id];
      //   let selection = {};
      //   if (original.waveNumber) {
      //     ids = pickSlipList
      //       .filter((x) => x.waveNumber === original.waveNumber)
      //       .map((x) => x.id);
      //   }
      //   if (props.row.getIsSelected()) {
      //     const array = Object.keys(customRowSelection)
      //       .filter((x) => !ids.includes(parseInt(x)))
      //       .map((x) => x);
      //     array.forEach((x) => {
      //       selection[x] = true;
      //     });
      //     setCustomRowSelection(selection);
      //   } else {
      //     ids.forEach((x) => {
      //       selection[x] = true;
      //     });
      //     setCustomRowSelection({ ...customRowSelection, ...selection });
      //   }
      // }}
    },
    ...columns,
  ];

  const queryClient = useQueryClient();

  const { refetch, isFetching } = useQuery({
    queryKey: ["task-pickslip", { customerCode, type }],
    queryFn: () => getTAPickSlip({ customerCode, type, basketId }),
    onSuccess: (data) => {
      setPickSlipList(data.data.data);
    },
    enabled: Boolean(customerCode),
  });
  const unallocateMutation = useMutation(taskUnallocate, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["task-pickslip"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });
  const confirmMutation = useMutation(taskConfirm, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["task-pickslip"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });
  const handleChange = useCallback((data) => {
    setSelectedIds(data);
  }, []);
  const onUnAllocate = () => {
    setDialog(
      <MessageDialog
        title={`Unallocate ${selectedIds.length > 30 ? "30" : selectedIds.length} Pickslip`}
        onClose={() => setDialog(null)}
        onOk={() => {
          if (selectedIds.length === 0) {
            setDialog(null);
            return;
          }
          const sliceArr = selectedIds.slice(0, 30);
          // if (selectedIds.length > 30) {
          //   setAlert(
          //     <Error_ content="Please select no more than 30 pickslip " onChange={setAlert} />
          //   );
          //   setDialog(null);
          //   return;
          // }
          //console.log(selectedIds);
          //return;
          unallocateMutation.mutate({
            customerCode,
            pickslips: sliceArr.map((x) => ({ pickslipId: x.id, waveNumber: x.waveNumber })),
          });
          setDialog(null);
        }}
      />
    );
  };
  const onConfirm = () => {
    setDialog(
      <MessageDialog
        title={`Confirm ${selectedIds.length} Pickslip`}
        onClose={() => setDialog(null)}
        onOk={() => {
          if (selectedIds.length === 0) {
            setDialog(null);
            return;
          }
          if (selectedIds.length > 10) {
            setAlert(
              <Error_ content="Please select no more than 10 pickslip " onChange={setAlert} />
            );
            setDialog(null);
            return;
          }
          //console.log(selectedIds);
          //return;
          confirmMutation.mutate({
            pickSlipIdList: selectedIds.map((x) => x.id),
          });
          setDialog(null);
        }}
      />
    );
  };
  // useEffect(() => {
  //   const newlIST = pickSlipList.filter((x) => filter.includes(x.orderNumber));

  //   if (newlIST.length > 0) {
  //     setPickSlipList(newlIST);
  //   }
  // }, [filter]);
  const unallocateStatus = unallocateMutation.status === "loading";
  const confirmStatus = confirmMutation.status === "loading";
  const downloadStatus = downloadReportMutation.status === "loading";
  const downloadWaveStatus = downloadWaveReportMutation.status === "loading";
  return (
    <>
      {/* <Fieldset label="List">
        <Grid sx={{ marginBottom: "1rem" }} container spacing={3}>
          <Grid item>
            <MDButton color="error" variant="outlined" onClick={onUnAllocate}>
              Unallocate
            </MDButton>
          </Grid>
          <Grid item>
            <TextField
              placeholder="Search Pick Slip Number"
              InputProps={{
                endAdornment: (
                  <InputAdornment>
                    <Search fontSize="medium" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <StripedCheckBoxDataGrid
          columns={columns}
          rows={pickSlipList}
          onRowSelectionModelChange={(id) => setSelectedIds(id)}
        />
      </Fieldset> */}
      {/* <Fieldset label="list">
         
      </Fieldset> */}
      {dialog}
      {(isFetching ||
        unallocateStatus ||
        confirmStatus ||
        downloadStatus ||
        downloadWaveStatus) && <LoadingDialog />}
      <PickslipLog {...logProp} />
      <PickslipPickScanLog {...pickLogProp} />
      <PickslipPackScanLog {...packLogProp} />

      <TableCard sx={{ marginTop: 4 }}>
        <Grid sx={{ padding: "10px 0 0 10px" }} container spacing={3}>
          <Grid item>
            <MDButton
              color="error"
              variant="gradient"
              onClick={onUnAllocate}
              disabled={selectedIds.length === 0}
            >
              Unallocate
            </MDButton>
          </Grid>

          <Grid item>
            <MDButton
              color="info"
              variant="gradient"
              onClick={onConfirm}
              disabled={selectedIds.length === 0 || type === "CANCEL"}
            >
              Manual Packing Confirm
            </MDButton>
          </Grid>
          <Grid item>
            <Texts
              field={{ name: "basketId", value: basketId, label: "Search by basket id" }}
              onChange={(e) => {
                let value = e.target.value.trim();
                setBasketId(value);
              }}
            />
          </Grid>
          <Grid item>
            <MDButton color="info" variant="gradient" onClick={refetch} disabled={isFetching}>
              Search
            </MDButton>
          </Grid>
        </Grid>
        <Grid sx={{ paddingLeft: "12px" }}>
          <Grid item>
            <FormControl>
              <RadioGroup
                row
                onChange={(e) => {
                  setType(e.target.value);
                }}
              >
                <FormControlLabel
                  labelPlacement="end"
                  value="ALL"
                  label="All"
                  control={
                    <Radio
                      sx={{ "& .MuiSvgIcon-root": { border: "1px solid #1A73E8" } }}
                      checked={type === "ALL"}
                    />
                  }
                />
                <FormControlLabel
                  labelPlacement="end"
                  value="CANCEL"
                  label="Cancel"
                  control={
                    <Radio
                      sx={{ "& .MuiSvgIcon-root": { border: "1px solid #1A73E8" } }}
                      checked={type === "CANCEL"}
                    />
                  }
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>

        <CheckboxTable2
          data={pickSlipList}
          columns={columns1}
          onSelectedRowsChange={handleChange}
        />
      </TableCard>
    </>
  );
}

const Badge = ({ color, content, onClick }) => {
  return (
    <MDBadge
      sx={{ cursor: "pointer" }}
      onClick={onClick}
      variant="gradient"
      color={color}
      size="xs"
      badgeContent={content}
      container
    />
  );
};

export default Pickslip;
